/**
 * GR 오답 근거 문제 화면(총괄 DEV-I-010, 디자인 근거 문제 화면 v0.1). 상태는 App이 갖고, 이 함수는 상태로 화면 하나를 만든다.
 *  - 원래 문장은 원래 문제의 보기 묶음 그대로(정답으로 채우지 않음, 정오·해설·근거 구절 없음)
 *  - 근거 문제·보기는 API 문구 그대로, 배정 ID·지점 ID로 연결. 최초 GR 결과와 근거 응답 결과는 따로
 *  - 제출된 응답의 결과가 null이면 제출 사실만(미제출·오답으로 보이지 않음)
 *  - 보기 고르기와 제출은 따로. 고른 보기는 탭 메모리에만
 */
import type { ItemsResponse } from '../core/types.js';
import { EV_CHIP, EV_NOTE, EV_STOP, firstChoiceLabel, evidencePurposeLabel, evidenceBasisNote, type EvidenceAssignment, type EvidenceQuestion, type EvidenceResponse } from '../core/evidence-model.js';
import type { PendingSubmit } from '../core/evidence-submit.js';
import { h } from './dom.js';

export type EvidencePhase = 'loading' | 'ok' | 'error' | 'denied' | 'notfound' | 'notsubmitted' | 'account';
export interface EvidenceState {
  readonly assignmentId: string | null;
  readonly attemptId: string;
  phase: EvidencePhase;
  errorCause: string | null;
  busy: boolean;
  /** 진행 중 조회가 끝나면 한 번 더(제출 확정 직후, 확정 전에 시작된 조회로 끝내지 않게) */
  again: boolean;
  seq: number;
  ev: EvidenceResponse | null;
  items: ItemsResponse | null;
  checkedAt: number | null;
}
export const blankEvidence = (assignmentId: string | null, attemptId: string): EvidenceState =>
  ({ assignmentId, attemptId, phase: 'loading', errorCause: null, busy: false, again: false, seq: 0, ev: null, items: null, checkedAt: null });

/** 이 탭에만 있는 값(서버에 저장되지 않음). 로그아웃·다른 계정 로그인 때 지운다 */
export interface EvidenceTab {
  owner: string | null;
  /** 배정 ID → 고른 보기 ID(미제출) */
  readonly drafts: Map<string, string>;
  /** 배정 ID → 거부 안내. fresh: 거부 직후 조회 한 번에만 보임 */
  readonly notes: Map<string, { kind: keyof typeof EV_NOTE; fresh: boolean }>;
  /** 이 탭에서 제출이 확정된 배정(200·already_answered). already: 서버에 이미 있던 답 */
  readonly confirmed: Map<string, { attemptId: string; already: boolean }>;
  /** 확정됐지만 아직 조회로 answered를 보지 못한 배정 */
  readonly awaiting: Set<string>;
  /** (DEV-I-011) question_not_provided 거부 뒤 재조회 성공을 기다리는 배정(그동안 선택·제출 불가, '다시 불러왔어요' 문구 없음) */
  readonly reload: Set<string>;
  /** (DEV-I-011) 키 충돌 뒤 학생이 '다시 확인'을 누른 시각(그 뒤 시작한 조회만 확인으로 침) */
  readonly conflictAsked: Map<string, number>;
  /** (DEV-I-011) 키 충돌 뒤 '다시 확인'으로 시작한 조회가 성공했고 그 배정이 open이었던 마지막 시각(이 기기 시계) */
  readonly conflictSeen: Map<string, number>;
}
export const blankTab = (): EvidenceTab => ({ owner: null, drafts: new Map(), notes: new Map(), confirmed: new Map(), awaiting: new Set(), reload: new Set(), conflictAsked: new Map(), conflictSeen: new Map() });

export interface EvidenceHandlers {
  readonly refresh: () => void;
  readonly toResult: () => void;
  readonly toList: () => void;
  readonly select: (assignmentId: string, optionId: string, displayNumber: string, no: number) => void;
  readonly submit: (assignmentId: string) => void;
  readonly recheck: (assignmentId: string) => void;
  readonly pending: (assignmentId: string) => PendingSubmit | undefined;
}

const hhmm = (t: number) => { const d = new Date(t); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

export function renderEvidence(S: EvidenceState, T: EvidenceTab, H: EvidenceHandlers, lastQ: Map<string, EvidenceQuestion>): HTMLElement {
  const title = S.items?.release.title ?? '근거 문제';
  const root = h('div', { class: 'result evidence', 'data-testid': 'evidence', 'data-phase': S.phase, 'data-att': S.attemptId });
  root.append(h('div', { class: 'ahead' }, h('span', { class: 'mark', 'aria-hidden': 'true' }), h('span', { class: 't' }, title),
    h('button', { class: 'exit', 'data-testid': 'ev-back', onclick: H.toResult }, '결과로')));
  const main = h('div', { class: 'rmain' });
  const side = h('aside', { class: 'rside', 'aria-label': '근거 문제 목록' });
  root.append(h('div', { class: 'body' }, h('div', { class: 'cols' }, main, side)));
  main.append(h('div', { class: 'rhead' }, h('b', {}, '근거 문제'), h('span', {}, '근거 문제 결과는 최초 GR 결과를 바꾸지 않아요. 따로 보여요.')));
  const sideBack = () => side.append(
    h('button', { class: 'secondary', 'data-testid': 'ev-back-side', onclick: H.toResult }, 'GR 결과로'),
    h('button', { class: 'secondary', 'data-testid': 'to-list', onclick: H.toList }, '과제 목록으로'));
  const anyPending = [...(S.ev?.assignments ?? [])].some((a) => H.pending(a.assignmentId)?.inflight === true);

  if (S.phase !== 'ok' || S.ev === null) {
    // 이 탭에서 제출이 확정됐는데 결과 조회가 실패: 제출 완료·결과 확인 실패를 따로 알린다(다시 제출하지 않음)
    const waiting = [...T.awaiting].filter((aid) => T.confirmed.get(aid)?.attemptId === S.attemptId);
    if (S.phase === 'error' && waiting.length > 0) {
      main.append(h('section', { class: 'rstat', 'data-k': 'error', 'data-testid': 'rstat', 'data-state': 'submitted-check-failed', role: 'alert' },
        h('h2', { 'data-testid': 'rstat-title' }, '제출은 됐어요 · 결과를 확인하지 못했어요'),
        h('p', { 'data-testid': 'rstat-body' }, `제출한 근거 응답은 그대로예요. ${S.errorCause === 'timeout' ? '정해진 시간 안에 결과를 받아 오지 못했어요.' : '결과를 받아 오지 못했어요.'} 다시 확인해 주세요.`),
        h('div', { class: 'rcheck' }, h('button', { class: 'secondary', 'data-testid': 'ev-retry', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '결과 다시 확인'))));
    } else {
      const errBody = S.errorCause === 'timeout' ? '다시 확인해 주세요. 결과 대기가 아니라 정해진 시간 안에 받아 오지 못한 상태예요.'
        : S.errorCause === 'network' ? '연결을 확인한 뒤 다시 확인해 주세요.' : '잠시 문제가 생겼어요. 다시 확인해 주세요.';
      const m: Record<Exclude<EvidencePhase, 'ok'>, [string, string | null]> = {
        loading: ['불러오고 있어요', null],
        error: [S.errorCause === 'timeout' ? '확인하지 못했어요.' : '불러오지 못했어요', errBody],
        denied: ['이 과제는 볼 수 없어요', '과제 목록에서 다시 확인해 주세요.'],
        account: ['계정 상태를 확인하고 있어요', null],
        notfound: ['찾을 수 없어요', '과제 목록에서 다시 열어 주세요.'],
        notsubmitted: ['아직 제출하지 않은 풀이예요', '제출한 뒤에 볼 수 있어요.'],
      };
      const [t, b] = S.phase === 'ok' ? m.loading : m[S.phase];
      main.append(h('section', { class: 'rstat', 'data-k': S.phase === 'error' ? 'error' : 'pending', 'data-testid': 'rstat', 'data-state': S.phase, role: S.phase === 'error' ? 'alert' : 'status' },
        h('h2', { 'data-testid': 'rstat-title' }, t), b !== null ? h('p', { 'data-testid': 'rstat-body' }, b) : null,
        S.phase === 'error' ? h('div', { class: 'rcheck' }, h('button', { class: 'secondary', 'data-testid': 'ev-retry', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인')) : null));
    }
    sideBack();
    return root;
  }

  const as = S.ev.assignments;
  const checkRow = (testid: string) => h('div', { class: 'rcheck', 'data-testid': testid },
    h('span', { 'data-testid': 'checked-at' }, S.checkedAt !== null ? `확인한 시각 ${hhmm(S.checkedAt)} (이 기기 시계)` : ''),
    h('button', { class: 'secondary', 'data-testid': 'ev-refresh', disabled: S.busy || anyPending, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인'));
  if (as.length === 0) {
    main.append(h('section', { class: 'rstat', 'data-k': 'pending', 'data-testid': 'rstat', 'data-state': 'empty' },
      h('h2', { 'data-testid': 'rstat-title' }, '현재 제공된 근거 문제가 없어요.'), checkRow('ev-check')));
    sideBack();
    return root;
  }
  main.append(checkRow('ev-check'));
  // 원래 문장(참고): 지점은 원래 문제처럼 보기 묶음만. 근거 문제가 있는 지점만 테두리를 진하게
  for (const it of (S.items?.items ?? []).filter((i) => i.responseMode === 'gr-points').sort((x, y) => x.order - y.order)) {
    const para = h('p', {});
    for (const s of it.segments ?? []) {
      if (s.kind === 'text') { para.append(s.text); continue; }
      const gp = (it.grPoints ?? []).find((x) => x.id === s.pointId);
      if (gp === undefined) continue;
      const on = as.some((a) => a.pointId === gp.id);
      const labels = [...gp.options].sort((x, y) => x.order - y.order).map((o) => o.label);
      const last = labels.pop() ?? '';
      para.append(h('span', { class: 'refpt' + (on ? ' on' : ''), 'data-testid': `ref-${gp.id}` }, h('span', { class: 'pt-no' }, gp.displayNumber),
        labels.length > 0 ? `[ ${labels.join(' / ')} / ` : '[ ', h('span', { class: 'nb' }, `${last} ]`)));
    }
    main.append(h('section', { class: 'evref', 'data-testid': 'evref' }, h('h3', {}, `원래 문장 · ${it.number}번 (참고)`), para));
  }
  for (const a of as) main.append(card(a, S, T, H, lastQ));
  side.append(h('h3', {}, '근거 문제'), h('ul', { class: 'evlist', 'data-testid': 'evlist' }, ...as.map((a) => h('li', {},
    h('button', {
      'data-testid': `evrow-${a.assignmentId}`,
      onclick: () => {
        const c = document.querySelector<HTMLElement>(`[data-testid="evcard-${a.assignmentId}"]`);
        if (c === null) return;
        c.scrollIntoView({ block: 'start' });
        c.querySelector<HTMLElement>('.evo:not(:disabled), button:not(:disabled)')?.focus();
      },
    }, h('span', { class: 'n' }, a.displayNumber), h('span', { class: 's' }, rowWord(a, T, H)))))));
  sideBack();
  return root;
}

function rowWord(a: EvidenceAssignment, T: EvidenceTab, H: EvidenceHandlers): string {
  const p = H.pending(a.assignmentId);
  if (p !== undefined) return p.phase === 'stalled' || p.phase === 'key_conflict' ? '제출 확인 못 함' : '제출 확인 중';
  if (a.status === 'answered') return a.answer?.result === 'correct' ? '제출함 · ✓ 맞음' : a.answer?.result === 'incorrect' ? '제출함 · ✕ 틀림' : '제출함';
  if (a.status === 'open' && T.awaiting.has(a.assignmentId)) return '제출함 · 결과 확인 중';
  if (a.status === 'open' && T.drafts.has(a.assignmentId)) return '고름 · 아직 제출 안 함';
  return EV_CHIP[a.status];
}

function card(a: EvidenceAssignment, S: EvidenceState, T: EvidenceTab, H: EvidenceHandlers, lastQ: Map<string, EvidenceQuestion>): HTMLElement {
  const aid = a.assignmentId;
  const p = H.pending(aid);
  const note = T.notes.get(aid);
  const shown = p !== undefined ? 'open' : a.status;
  const el = h('section', { class: 'evcard', 'data-s': shown, 'data-status': a.status, 'data-testid': `evcard-${aid}`, 'data-point': a.pointId, 'aria-labelledby': `evh-${aid}` });
  const chip = p !== undefined ? (p.phase === 'stalled' || p.phase === 'key_conflict' ? '제출 확인 못 함' : '제출 확인 중') : EV_CHIP[a.status];
  el.append(h('div', { class: 'evhead' }, h('span', { class: 'pt-no', 'aria-hidden': 'true' }, a.displayNumber), h('h2', { id: `evh-${aid}` }, `${a.displayNumber}번 지점 근거 문제`),
    h('span', { class: 'evchip', 'data-s': a.status, 'data-testid': 'evchip' }, chip)));
  el.append(h('p', { class: 'evmine', 'data-testid': 'evpurpose' }, evidencePurposeLabel(a)));
  const basisNote = evidenceBasisNote(a);
  if (basisNote !== null) el.append(h('p', { class: 'evbody', 'data-testid': 'evbasis' }, basisNote));
  const first = firstChoiceLabel(S.items, a.pointId);
  if (first !== null) el.append(h('p', { class: 'evmine', 'data-testid': 'evfirst' }, '처음 고른 보기 ', h('b', {}, first)));
  // 거부 안내는 answered가 아닐 때만(과거 거부 응답보다 최신 조회의 제출됨이 우선)
  if (note !== undefined && a.status !== 'answered' && p === undefined) el.append(h('div', { class: 'evstate', 'data-k': 'warn', role: 'alert', 'data-testid': 'evnote' }, EV_NOTE[note.kind]));
  // question_not_provided 거부 뒤 재조회 성공 전: 다시 불러왔다고 쓰지 않고, 선택·제출을 막는다
  const reloading = T.reload.has(aid) && p === undefined && a.status !== 'answered';
  if (reloading) el.append(h('div', { class: 'evstate', 'data-k': 'warn', role: 'alert', 'data-testid': 'evnote', 'data-kind': 'reload-pending' },
    S.busy ? '제출되지 않았어요. 문제를 다시 불러오고 있어요.' : '제출되지 않았어요. 문제를 다시 불러오지 못했어요. 다시 확인해 주세요.'));
  const stop = EV_STOP[a.status];
  if (stop !== undefined && p === undefined) { el.append(h('p', { class: 'evbody', 'data-testid': 'evbody' }, h('b', {}, stop[0]), h('br'), stop[1])); return el; }
  if (a.status === 'answered' && (a.question === undefined || a.answer?.result == null)) {
    // 제출됨, 결과 null(근거 문제 사용 중단 등): 제출 사실만. 미제출·오답으로 보이지 않음
    el.append(h('p', { class: 'evbody', 'data-testid': 'evbody' }, h('b', {}, '제출했어요'), h('br'), '지금은 이 근거 문제와 결과를 보여 줄 수 없어요. 제출 기록은 그대로예요.'));
    return el;
  }
  if (a.status === 'open' && p === undefined && T.awaiting.has(aid)) {
    // 이 탭에서 제출이 확정됐는데 이 조회는 그 전에 시작됨: 다시 제출하지 못하게 하고 다음 조회로 확인
    el.append(h('p', { class: 'evbody', 'data-testid': 'evbody' }, h('b', {}, '제출했어요'), h('br'), '결과를 확인하고 있어요.'));
    return el;
  }
  const q = a.question ?? lastQ.get(aid);
  if (q === undefined) return el;
  lastQ.set(aid, q);
  el.append(h('p', { class: 'evstem', 'data-testid': 'evstem' }, q.stem));
  const answered = a.status === 'answered' && p === undefined;
  const chosen = answered ? (a.answer?.selectedOptionId ?? null) : p !== undefined ? p.optionId : (T.drafts.get(aid) ?? null);
  const locked = answered || p !== undefined || a.status !== 'open' || reloading;
  const opts = [...q.options].sort((x, y) => x.no - y.no);
  el.append(h('div', { class: 'evopts', role: 'radiogroup', 'aria-label': q.stem, 'data-testid': 'evopts' }, ...opts.map((o) => h('button', {
    class: 'evo', role: 'radio', 'aria-checked': String(chosen === o.id), 'data-opt': o.id, 'data-testid': `evo-${aid}-${o.no}`, disabled: locked,
    tabindex: chosen === o.id || (chosen === null && o.no === 1) ? '0' : '-1',
    onclick: () => { if (!locked) H.select(aid, o.id, a.displayNumber, o.no); },
    onkeydown: (e: KeyboardEvent) => {
      const d = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
      if (d === 0) return;
      e.preventDefault();
      const all = [...(e.currentTarget as HTMLElement).parentElement!.children] as HTMLElement[];
      const i = all.indexOf(e.currentTarget as HTMLElement);
      all[Math.max(0, Math.min(all.length - 1, i + d))]!.focus();
    },
  }, h('span', { class: 'c', 'aria-hidden': 'true' }, String(o.no)), h('span', { class: 't' }, o.text, answered && chosen === o.id ? h('span', { class: 'mine' }, '내가 고른 보기') : null)))));
  if (answered) {
    const r = a.answer!.result!;
    el.append(h('div', { class: 'evres', 'data-testid': 'evres', 'data-r': r }, h('span', {}, '근거 응답 결과'), h('span', { class: 'r', 'data-r': r }, r === 'correct' ? '✓ 맞음' : '✕ 틀림'), h('small', {}, '최초 GR 결과와 따로 기록돼요')));
    if (T.confirmed.get(aid)?.already === true) el.append(h('p', { class: 'evmine', 'data-testid': 'evalready' }, '이미 제출된 답이 있어 그 답과 결과를 보여 줘요.'));
    return el;
  }
  if (p !== undefined) {
    const stuck = p.phase === 'stalled' || p.phase === 'key_conflict';
    if (p.phase === 'key_conflict') {
      // DEV-I-011: 요청 키 충돌. 잠금·자동 재전송 중단·새 키 없음 그대로. '다시 확인'은 조회만
      const seen = T.conflictSeen.get(aid);
      el.append(h('div', { class: 'evstate', 'data-k': 'warn', role: 'status', 'data-testid': 'evpending', 'data-phase': p.phase },
        h('span', { 'data-testid': 'evconflict-main' }, '제출 정보를 확인하는 중 문제가 생겨 이 답을 자동으로 다시 보내지 않아요. 고른 보기는 바꿀 수 없어요.'),
        h('span', { 'data-testid': 'evconflict-sub' }, "‘다시 확인’은 제출된 답이 있는지 확인해요. 계속 이 안내가 나오면 선생님께 알려 주세요."),
        seen !== undefined ? h('span', { 'data-testid': 'evconflict-checked' }, `마지막 확인 ${hhmm(seen)}(이 기기 시계) · 조회 결과에서 제출된 답을 확인하지 못했어요.`) : null,
        h('button', { class: 'secondary', 'data-testid': 'ev-recheck', disabled: S.busy, onclick: () => H.recheck(aid) }, S.busy ? '확인하는 중' : '다시 확인')));
      return el;
    }
    el.append(h('div', { class: 'evstate', 'data-k': stuck ? 'warn' : 'info', role: 'status', 'data-testid': 'evpending', 'data-phase': p.phase },
      p.phase === 'sending' ? '제출하고 있어요.' : stuck ? '제출됐는지 아직 확인하지 못했어요. 고른 보기는 바꿀 수 없어요.' : '제출됐는지 확인하고 있어요. 고른 보기는 바꿀 수 없어요.',
      stuck ? h('span', {}, '다시 확인하면 같은 답으로 확인해요. 새로 제출하지 않아요.') : null,
      stuck ? h('button', { class: 'secondary', 'data-testid': 'ev-recheck', onclick: () => H.recheck(aid) }, '다시 확인') : null));
    return el;
  }
  if (a.status !== 'open') return el;
  const draft = T.drafts.get(aid);
  el.append(h('div', { class: 'evacts' },
    h('span', { class: 'draft', 'data-testid': 'evdraft' }, draft !== undefined ? '아직 제출하지 않았어요. 고른 보기는 이 탭에만 남아 있어요.' : '보기를 하나 고른 뒤 제출해 주세요.'),
    h('button', { class: 'primary', 'data-testid': `evsubmit-${aid}`, disabled: draft === undefined, onclick: () => H.submit(aid) }, '제출하기')));
  return el;
}
